// turnSignals.js — detector PURO de sinais de intenção de um turno do chat do Code.
//
// PROPÓSITO: dado o texto que o usuário mandou, classifica a INTENÇÃO do turno em
// quatro dimensões independentes (readOnly / build / risky / chat) para o "Turn
// Role Router" decidir o papel/postura do agente (análise vs. modificação vs.
// confirmação de algo perigoso vs. papo curto). NÃO decide nada sozinho — só
// levanta os sinais e devolve a EVIDÊNCIA (os cues casados) pra explicar a decisão
// ao usuário depois.
//
// É PURO POR CONSTRUÇÃO: zero imports, zero efeitos, nunca lança. Texto não-string
// vira '' e todos os sinais saem false. Matching é case-insensitive; para tokens
// curtos de saudação/ack (oi, ok, …) usamos limite de palavra (\b) pra "ok" NÃO
// casar dentro de "book"/"broke"; para frases distintivas multi-palavra um
// substring case-insensitive basta.

/**
 * @typedef {Object} TurnSignals
 * @property {boolean} readOnly  intenção de análise/leitura (explicar, revisar, …)
 * @property {boolean} build     intenção de modificação (implementar, corrigir, …)
 * @property {boolean} risky     ação perigosa/irreversível/externa (deploy, push, …)
 * @property {boolean} chat      papo curto / saudação / ack
 * @property {string[]} evidence cues casados, deduplicados, em ordem de detecção
 */

// ── cues de palavra-inteira (\b…\b) ────────────────────────────────────────
// Usados para tokens curtos onde um substring casaria dentro de outra palavra.
const READONLY_WORD_CUES = [
  'explique', 'explica', 'analise', 'analisa', 'porque', 'mostre', 'mostra',
  'leia', 'revise', 'revisa', 'descreva', 'resuma', 'entenda',
  'explain', 'why', 'review', 'read', 'describe', 'summarize',
];

const BUILD_WORD_CUES = [
  'implemente', 'implementa', 'conserte', 'conserta', 'corrija', 'corrige',
  'adicione', 'adiciona', 'crie', 'cria', 'refatore', 'refatora', 'escreva',
  'escreve', 'mude', 'muda', 'altere', 'altera', 'atualize', 'atualiza',
  'gere', 'gera', 'remova', 'remove',
  'implement', 'fix', 'add', 'create', 'refactor', 'write', 'change',
  'update', 'build', 'make', 'rename',
];

const RISKY_WORD_CUES = [
  'deploy', 'deployar', 'publish', 'publicar', 'migration', 'migrate',
  'prod', 'secret', 'credentials',
];

const CHAT_WORD_CUES = [
  'oi', 'olá', 'ola', 'valeu', 'obrigado', 'obrigada', 'beleza', 'blz',
  'hi', 'hello', 'hey', 'cool', 'nice', 'ok', 'okay',
];

// ── cues de frase/substring (multi-palavra ou distintivos) ─────────────────
// Distintivos o suficiente pra um substring case-insensitive não dar falso-positivo.
const READONLY_PHRASE_CUES = [
  'o que faz', 'o que é', 'o que e', 'por que', 'como funciona',
  'what does', 'how does', 'show me', 'walk me through',
];

const RISKY_PHRASE_CUES = [
  'migração', 'migracao', 'db:migrate', 'git push', 'push --force',
  'force push', 'rm -rf', 'drop table', 'drop database', 'wrangler deploy',
  'npm publish', 'reset --hard', 'delete production', 'api key', '.env',
];

const CHAT_PHRASE_CUES = [
  'bom dia', 'boa tarde', 'boa noite', 'thanks', 'thank you', 'got it',
];

/**
 * Casa um cue de palavra-inteira (case-insensitive, com limite \b dos dois lados).
 * @param {string} lower texto já em lowercase
 * @param {string} cue
 * @returns {boolean}
 */
function matchesWord(lower, cue) {
  // Escapa metacaracteres de regex no cue (defensivo — cues são literais).
  const escaped = cue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`, 'i').test(lower);
}

/**
 * Acumula em `evidence` (deduplicado, ordem de detecção) os cues que casam.
 * @param {string} lower
 * @param {string[]} wordCues
 * @param {string[]} phraseCues
 * @param {string[]} evidence
 * @returns {boolean} true se algum cue casou
 */
function collectMatches(lower, wordCues, phraseCues, evidence) {
  let hit = false;
  for (const cue of wordCues) {
    if (matchesWord(lower, cue)) {
      hit = true;
      if (!evidence.includes(cue)) evidence.push(cue);
    }
  }
  for (const cue of phraseCues) {
    if (lower.includes(cue)) {
      hit = true;
      if (!evidence.includes(cue)) evidence.push(cue);
    }
  }
  return hit;
}

/**
 * Detecta os sinais de intenção de um turno.
 *
 * @param {{ text: string, priorTurnTouchedCode?: boolean }} ctx
 *   `text` não-string (number/obj/bool/null/undefined) é coagido a ''. Nunca lança.
 * @returns {TurnSignals}
 */
export function detectTurnSignals(ctx) {
  const raw = ctx && typeof ctx === 'object' ? ctx.text : '';
  // Só strings de verdade contam; qualquer outro tipo (number/obj/bool/null) vira
  // '' — NÃO stringificamos para "42"/"[object Object]"/"true" e classificamos lixo.
  const text = typeof raw === 'string' ? raw : '';
  const lower = text.toLowerCase();

  const evidence = [];

  const readOnly = collectMatches(lower, READONLY_WORD_CUES, READONLY_PHRASE_CUES, evidence);
  // build não tem cues de frase próprios — só palavras-inteiras.
  const build = collectMatches(lower, BUILD_WORD_CUES, [], evidence);
  const risky = collectMatches(lower, RISKY_WORD_CUES, RISKY_PHRASE_CUES, evidence);

  // chat: um cue de saudação/ack OU (turno muito curto e nenhum outro sinal).
  const greeted = collectMatches(lower, CHAT_WORD_CUES, CHAT_PHRASE_CUES, evidence);
  const trimmed = text.trim();
  const wordCount = trimmed ? trimmed.split(/\s+/).length : 0;
  const shortAndQuiet = wordCount > 0 && wordCount <= 3 && !readOnly && !build && !risky;
  const chat = greeted || shortAndQuiet;

  return { readOnly, build, risky, chat, evidence };
}
